import { Tag } from '@components';
import { Box, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { DocumentDownload, Trash } from 'iconsax-react';
import type { FC, KeyboardEvent, ReactElement } from 'react';

import Enums from '@core/types/api/Enums';
import type { File as SlabFile } from '@core/types/api/File/File';
import { type FileEntityType, fileEntityPathSegment } from '@core/types/file';
import { formatAttachmentBytes } from '@core/utils/emailAttachments';
import { formatDateTime } from '@core/utils/format';

import { DataTable } from '../DataTable/DataTable';
import type { ColumnConfig } from '../DataTable/TableDataModel';
import { useApiTableDataWithoutAggregations } from '../DataTable/useApiTableData';
import { FileTypeIcon } from './FileTypeIcon';

const buildFileListColumns = ({
  onDownload,
}: {
  onDownload: (file: SlabFile) => void;
}): ColumnConfig<SlabFile>[] => {
  const handleNameKeyDown = (event: KeyboardEvent<HTMLDivElement>, file: SlabFile): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onDownload(file);
    }
  };

  return [
    {
      id: 'fileName',
      label: 'Name',
      type: 'custom',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <Box
          role='button'
          tabIndex={0}
          onClick={() => onDownload(row)}
          onKeyDown={(event) => handleNameKeyDown(event, row)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            '&:hover .file-list-name': { textDecoration: 'underline' },
          }}
        >
          <FileTypeIcon fileName={row.fileName} />
          <Typography variant='body2' fontWeight={600} className='file-list-name' noWrap>
            {row.fileName}
          </Typography>
          {row.isSensitive && (
            <Tag
              variant='Red'
              label='Sensitive'
              aria-label={`${row.fileName} is a sensitive file`}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'sizeBytes',
      label: 'Size',
      type: 'custom',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <Typography variant='secondary'>{formatAttachmentBytes(row.sizeBytes)}</Typography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Uploaded',
      type: 'custom',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <Typography variant='secondary'>{formatDateTime(row.createdAt)}</Typography>
      ),
    },
  ];
};

type Props = {
  entityType: FileEntityType;
  entityId: string;
  onDownload: (file: SlabFile) => void;
  onDelete: (file: SlabFile) => void;
  isDeleting?: boolean;
  /** Id of the file whose download is being fetched; that row shows a spinner and its
   * download action is disabled. */
  downloadingFileId?: string | null;
};

/** Paginated list of a single entity's attached files: name (click to download),
 * size, upload date, file-type icon, and download/delete actions. */
export const FileList: FC<Props> = ({
  entityType,
  entityId,
  onDownload,
  onDelete,
  isDeleting = false,
  downloadingFileId = null,
}) => {
  const theme = useTheme();

  const tableModel = useApiTableDataWithoutAggregations({
    rowsRouteKey: 'GET entity files by entity ID',
    rowsPathParams: { entityPath: fileEntityPathSegment(entityType), id: entityId },
    initialSortBy: { name: 'createdAt', direction: Enums.SortDirection.Descending },
    extractRows: (list) => list.items,
    makeColumnConfigs: () => buildFileListColumns({ onDownload }),
  });

  return (
    <DataTable
      tableName='attached-files'
      tableModel={tableModel}
      showToolbar={false}
      renderStickyActions={(row): ReactElement => {
        const isRowDownloading = downloadingFileId === row.id;
        return (
          <>
            <IconButton
              aria-label={`Download ${row.fileName}`}
              disabled={isRowDownloading}
              onClick={() => onDownload(row)}
            >
              {isRowDownloading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <DocumentDownload variant='TwoTone' color={theme.palette.SlabIndigo[700]} />
              )}
            </IconButton>
            <IconButton
              aria-label={`Delete ${row.fileName}`}
              disabled={isDeleting}
              onClick={() => onDelete(row)}
            >
              <Trash variant='TwoTone' color={theme.palette.SlabIndigo[700]} />
            </IconButton>
          </>
        );
      }}
    />
  );
};
